// Central MUI theme for the HMI: color token palettes (light/dark), typography,
// app-wide component overrides (<Alert>, pickers), and the shared accent/severity
// chip style helpers used across chat, feed, and inspector surfaces.
import { createContext, useMemo, useState } from "react";
import { createTheme } from "@mui/material/styles";

// Core accent palettes, shared between light and dark mode.
const teal = {
    100: "#cedfe1",
    200: "#9ebec2",
    300: "#6d9ea4",
    400: "#3d7d85",
    500: "#0c5d67",
    600: "#0a4a52",
    700: "#07383e",
    800: "#052529",
    900: "#021315"
};

const red = {
    100: "#f1d6d5",
    200: "#e2adab",
    300: "#d48582",
    400: "#c55c58",
    500: "#b7332e",
    600: "#922925",
    700: "#6e1f1c",
    800: "#491412",
    900: "#250a09"
};

const orange = {
    100: "#fadbd2",
    200: "#f5b8a5",
    300: "#f09479",
    400: "#eb714c",
    500: "#e64d1f",
    600: "#b83e19",
    700: "#8a2e13",
    800: "#5c1f0c",
    900: "#2e0f06"
};

const purple = {
    100: "#e3d3dd",
    200: "#c7a7bb",
    300: "#aa7a98",
    400: "#8e4e76",
    500: "#722254",
    600: "#5b1b43",
    700: "#441432",
    800: "#2e0e22",
    900: "#170711"
};

const beige = {
    100: "#b6ab82",
    200: "#c6bd99",
    300: "#d6cfb0",
    400: "#e6e1c7",
    500: "#f6f3de",
    600: "#f9f6e5",
    700: "#fcf9ec",
    800: "#fefbf2",
    900: "#fffdf8",
};

/**
 * App-wide <Alert> palette: single source of truth for every alert banner in
 * the HMI. The MuiAlert override in themeSettings() applies these per severity;
 * a local `sx` on an individual <Alert> still wins.
 * @param {'light'|'dark'} mode - Palette mode.
 * @returns {Object} Per-severity {bg, text, icon, border} colors.
 */
export const alertTokens = (mode) => {
  const isDark = mode === 'dark';
  return {
    success: {
      // Dark mode uses a translucent teal overlay so it pops on the near-black bg.
      bg: isDark ? 'rgba(38, 198, 218, 0.12)' : 'rgba(12, 93, 103, 0.10)',
      text: isDark ? '#cedfe1' : teal[700],
      icon: isDark ? teal[500] : teal[500],
      border: isDark ? 'rgba(38, 198, 218, 0.35)' : 'rgba(12, 93, 103, 0.25)',
    },
    warning: {
      bg: isDark ? 'rgba(230, 77, 31, 0.12)' : 'rgba(230, 77, 31, 0.10)',
      text: isDark ? '#fadbd2' : orange[700],
      icon: isDark ? orange[400] : orange[500],
      border: isDark ? 'rgba(230, 77, 31, 0.35)' : 'rgba(230, 77, 31, 0.25)',
    },
    error: {
      bg: isDark ? 'rgba(183, 51, 46, 0.14)' : 'rgba(183, 51, 46, 0.10)',
      text: isDark ? '#f1d6d5' : red[700],
      icon: isDark ? red[400] : red[500],
      border: isDark ? 'rgba(183, 51, 46, 0.35)' : 'rgba(183, 51, 46, 0.25)',
    },
    info: {
      // Cooler than success — no checkmark connotation.
      bg: isDark ? 'rgba(38, 198, 218, 0.08)' : 'rgba(12, 93, 103, 0.06)',
      text: isDark ? '#cedfe1' : teal[800],
      icon: isDark ? '#26c6da' : teal[500],
      border: isDark ? 'rgba(38, 198, 218, 0.30)' : 'rgba(12, 93, 103, 0.20)',
    },
  };
};

export const tokens = (mode) => ({
  ...(mode === "light"
    ? {
        primary: {
          100: "#ffffff",
          200: "#dcdcdc",
          300: "#bcbcbc",
          400: "#9d9d9d",
          500: "#7d7d7d",
          600: "#5e5e5e",
          700: "#454545",
          800: "#2d2d2d",
          900: "#1a1a1a",
          1000: "#0b0b0b",
        },
        grey: {
          100: "#f6f6f6",
          200: "#e0e0e0",
          300: "#c2c2c2",
          400: "#a3a3a3",
          500: "#858585",
          600: "#5f5f5f",
          700: "#3b3b3b",
          800: "#222222",
          900: "#141414",
        },

        beige: {
          100: "#fffdf8",
          200: "#fefbf2",
          300: "#fcf9ec",
          400: "#f9f6e5",
          500: "#f6f3de",
          600: "#e6e1c7",
          700: "#d6cfb0",
          800: "#c6bd99",
          900: "#b6ab82",
        },
        beigeAccent: beige,
        tealAccent: teal,
        redAccent: red,
        orangeAccent: orange,
        purpleAccent: purple,
      }
    : {
        primary: {
          0: "#0b0b0b",
          100: "#1a1a1a",
          200: "#2d2d2d",
          300: "#454545",
          400: "#5e5e5e",
          500: "#7d7d7d",
          600: "#9d9d9d",
          700: "#bcbcbc",
          800: "#dcdcdc",
          900: "#ffffff",
        },
        grey: {
          100: "#141414",
          200: "#222222",
          300: "#3b3b3b",
          400: "#5f5f5f",
          500: "#858585",
          600: "#a3a3a3",
          700: "#c2c2c2",
          800: "#e0e0e0",
          900: "#f6f6f6",
        },
        beige: {
          100: "#b6ab82",
          200: "#c6bd99",
          300: "#d6cfb0",
          400: "#e6e1c7",
          500: "#f6f3de",
          600: "#f9f6e5",
          700: "#fcf9ec",
          800: "#fefbf2",
          900: "#fffdf8",
        },
        beigeAccent: beige,
        tealAccent: teal,
        redAccent: red,
        orangeAccent: orange,
        purpleAccent: purple,
      }),
});

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.tealAccent[500],
            },
            neutral: {
              dark: colors.orangeAccent[700],
              main: colors.orangeAccent[500],
              light: colors.orangeAccent[100],
            },
            background: {
              default: colors.primary[100],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[100],
            },
            secondary: {
              main: colors.tealAccent[500],
            },
            neutral: {
              dark: colors.orangeAccent[700],
              main: colors.orangeAccent[500],
              light: colors.orangeAccent[100],
            },
            background: {
              default: colors.primary[100],
            },
          }),
    },
    typography: {
      fontFamily: ["Quarion", "sans-serif"].join(","),
      fontSize: 12,
      h1: { fontFamily: "Quarion, sans-serif", fontSize: 40 },
      h2: { fontFamily: "Quarion, sans-serif", fontSize: 32 },
      h3: { fontFamily: "Quarion, sans-serif", fontSize: 24 },
      h4: { fontFamily: "Quarion, sans-serif", fontSize: 20 },
      h5: { fontFamily: "Quarion, sans-serif", fontSize: 16 },
      h6: { fontFamily: "Quarion, sans-serif", fontSize: 14 },
      h7: { fontFamily: "Quarion, sans-serif", fontSize: 12 },
      h8: { fontFamily: "Quarion, sans-serif", fontSize: 8 },
    },
    components: {
      // Baseline <Alert> styling from alertTokens(); MUI's pastel defaults
      // clash with the AG Automation palette.
      MuiAlert: {
        defaultProps: {
          variant: 'standard',
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: '1px solid transparent',
            // MUI top-aligns icon/message/action, which looks lopsided on
            // single-line banners; center vertically (multi-line can opt out).
            alignItems: 'center',
            '& .MuiAlert-icon': { alignItems: 'center', py: 0 },
            '& .MuiAlert-message': { py: 0.5 },
            '& .MuiAlert-action': { alignItems: 'center', py: 0, mr: 0 },
          },
          standardSuccess: {
            backgroundColor: alertTokens(mode).success.bg,
            color: alertTokens(mode).success.text,
            borderColor: alertTokens(mode).success.border,
            '& .MuiAlert-icon': { color: alertTokens(mode).success.icon },
          },
          standardWarning: {
            backgroundColor: alertTokens(mode).warning.bg,
            color: alertTokens(mode).warning.text,
            borderColor: alertTokens(mode).warning.border,
            '& .MuiAlert-icon': { color: alertTokens(mode).warning.icon },
          },
          standardError: {
            backgroundColor: alertTokens(mode).error.bg,
            color: alertTokens(mode).error.text,
            borderColor: alertTokens(mode).error.border,
            '& .MuiAlert-icon': { color: alertTokens(mode).error.icon },
          },
          standardInfo: {
            backgroundColor: alertTokens(mode).info.bg,
            color: alertTokens(mode).info.text,
            borderColor: alertTokens(mode).info.border,
            '& .MuiAlert-icon': { color: alertTokens(mode).info.icon },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: { '&.Mui-selected': { backgroundColor: colors.tealAccent[500], '&:hover': { backgroundColor: colors.tealAccent[600] } } },
        },
      },
      MuiClockPointer: {
        styleOverrides: {
          root: { backgroundColor: colors.tealAccent[500] },
          thumb: { backgroundColor: colors.tealAccent[500], borderColor: colors.tealAccent[500] },
        },
      },
      MuiClock: {
        styleOverrides: {
          pin: { backgroundColor: colors.tealAccent[500] },
        },
      },
      MuiMultiSectionDigitalClockSection: {
        styleOverrides: {
          root: {
            '& .MuiMenuItem-root.Mui-selected': {
              backgroundColor: colors.tealAccent[500],
              color: '#fff',
              '&:hover': { backgroundColor: colors.tealAccent[600] },
            },
          },
        },
      },
      MuiDigitalClock: {
        styleOverrides: {
          root: {
            '& .MuiMenuItem-root.Mui-selected': {
              backgroundColor: colors.tealAccent[500],
              color: '#fff',
              '&:hover': { backgroundColor: colors.tealAccent[600] },
            },
          },
        },
      },
    },
  };
};

// Accent-chip design system, shared by ChatMessage (intro chips), CoPilotFeed
// (persona chips), and DebugInspector (kind-filter chips) so all stay in sync.
export const SEVERITY_TOKEN = {
  critical: "redAccent",
  warning:  "orangeAccent",
  info:     "tealAccent",
};

/**
 * Returns the shared accent-chip `sx` object.
 * @param {Object} colors - Theme tokens palette.
 * @param {string} token - Accent token name (e.g. "tealAccent").
 * @param {Object} [options] - {active?: boolean (muted variant when false),
 *   severity?: "info"|"warning"|"critical" (overrides token), height?, fontSize?}.
 * @returns {Object} MUI `sx` object for a <Chip>.
 */
export function accentChipSx(colors, token, options = {}) {
  const { active = true, severity = null, height = 22, fontSize = "0.7rem" } = options;
  const resolvedToken = severity ? (SEVERITY_TOKEN[severity] || token) : token;
  const accent = colors[resolvedToken]?.[500] || colors.tealAccent[500];
  if (!active) {
    return {
      height,
      fontSize,
      fontWeight: 600,
      backgroundColor: `${colors.primary[400]}40`,
      color: colors.primary[800],
      "& .MuiChip-icon":  { color: "inherit", fontSize: 14, ml: 0.5, mr: -0.25 },
      "& .MuiChip-label": { px: 0.75 },
    };
  }
  return {
    height,
    fontSize,
    fontWeight: 600,
    backgroundColor: `${accent}40`,
    color: accent,
    "& .MuiChip-icon":  { color: "inherit", fontSize: 14, ml: 0.5, mr: -0.25 },
    "& .MuiChip-label": { px: 0.75 },
    "&:hover": { backgroundColor: `${accent}60` },
  };
}

/**
 * Compact severity chip `sx` (bulletin headers, chat message footers) — same
 * alpha pattern as accentChipSx but reduced height and icon size.
 * @param {Object} colors - Theme tokens palette.
 * @param {"info"|"warning"|"critical"} [severity="info"] - Severity level.
 * @returns {Object} MUI `sx` object for a <Chip>.
 */
export function severityChipSx(colors, severity = "info") {
  const token = SEVERITY_TOKEN[severity] || "tealAccent";
  const accent = colors[token]?.[500] || colors.tealAccent[500];
  return {
    height: 20,
    fontSize: "0.65rem",
    fontWeight: 700,
    backgroundColor: `${accent}25`,
    color: accent,
    "& .MuiChip-icon":  { color: accent, fontSize: 13, ml: 0.5, mr: -0.25 },
    "& .MuiChip-label": { px: 0.75 },
  };
}

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

/**
 * Provides the MUI theme plus a color-mode toggle, persisting the chosen mode
 * to localStorage.
 * @returns {[import('@mui/material').Theme, {toggleColorMode: Function}]} Theme and toggle.
 */
export const useMode = () => {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem("colorMode");
    return saved === "dark" || saved === "light" ? saved : "light";
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === "light" ? "dark" : "light";
          localStorage.setItem("colorMode", next);
          return next;
        }),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};